import { useContext, useEffect, useRef, useState } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import Enums from '@core/types/api/Enums';
import {
  type QuoteEditLock,
  type QuoteEditLockChangedEvent,
  QuoteEditLockStatus,
} from '@core/types/quoteEditLock';
import { hasValue } from '@core/utils/equality';
import { isQuoteLockedError } from '@core/utils/query';
import { isValidUUID } from '@core/utils/uuid';
import {
  QUOTE_EDIT_LOCK_EXPIRY_GRACE_MS,
  QUOTE_EDIT_LOCK_FALLBACK_MESSAGE,
  QUOTE_EDIT_LOCK_HEARTBEAT_MS,
} from '@web/constants/app';
import { useInvalidateCache } from '@web/hooks/useInvalidateCache';
import { useSlabAuth } from '@web/hooks/useSlabAuth';
import { useSlabMutation } from '@web/hooks/useSlabMutation';
import { useSlabQuery } from '@web/hooks/useSlabQuery';
import { useSlabSocket } from '@web/hooks/useSlabSocket';
import { pageClientId } from '@web/utils/pageClientId';
import { releaseQuoteEditLockOnPageHide } from '@web/utils/releaseQuoteEditLockOnPageHide';
import { SlabConfig } from '@web/utils/SlabConfig';

/** Who holds the quote's edit lock right now, kept live over the websocket. */
export const useQuoteEditLockState = (
  quoteId: string,
): { lock: QuoteEditLock | null; isLockedByOther: boolean; isFree: boolean } => {
  const ctx = useContext(SlabContext);
  const { invalidateCache } = useInvalidateCache();

  const { data, isSuccess } = useSlabQuery(
    'GET quote edit lock',
    { pathParams: { id: quoteId } },
    {
      enabled: isValidUUID(quoteId),
      // The expiry timer fires once, so a failed read would otherwise never be retried.
      refetchInterval: (query) =>
        query.state.status === 'error' ? QUOTE_EDIT_LOCK_HEARTBEAT_MS : false,
    },
  );
  const lock = data ?? null;

  useSlabSocket(
    () => [
      {
        method: Enums.WsEvent.QuoteEditLockChanged,
        action: (payload): void => {
          if ((payload as QuoteEditLockChangedEvent).quoteId === quoteId) {
            invalidateCache(['GET quote edit lock']);
          }
        },
      },
      {
        method: Enums.WsEvent.Reconnected,
        action: (): void => invalidateCache(['GET quote edit lock']),
      },
    ],
    [quoteId, invalidateCache],
  );

  // A holder that vanished sends no release event, so the lease runs out silently.
  const expiresAt = lock?.expiresAt;
  useEffect(() => {
    if (!hasValue(expiresAt)) return undefined;

    const msUntilExpiry = Math.max(Date.parse(expiresAt) - Date.now(), 0);
    const timer = window.setTimeout(
      () => invalidateCache(['GET quote edit lock']),
      msUntilExpiry + QUOTE_EDIT_LOCK_EXPIRY_GRACE_MS,
    );

    return (): void => window.clearTimeout(timer);
  }, [expiresAt, invalidateCache]);

  return {
    lock,
    isLockedByOther: hasValue(lock) && lock.editorId !== ctx.userInfo.user.id,
    isFree: isSuccess && !hasValue(lock),
  };
};

type QuoteEditLockOwnership = {
  status: QuoteEditLockStatus;
  /** The server's refusal text, set while another user holds the lock. */
  blockedMessage: string | null;
  editorName: string | null;
};

/**
 * Holds the quote's edit lock while `enabled`, renewing it on a heartbeat and releasing it when
 * the editor closes. While blocked, retakes it once freed and calls `onUnblocked` to reload the quote.
 */
export const useQuoteEditLockOwnership = (
  quoteId: string,
  enabled: boolean,
  onUnblocked?: () => void,
): QuoteEditLockOwnership => {
  const [status, setStatus] = useState(QuoteEditLockStatus.Inactive);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  const isWaiting = status === QuoteEditLockStatus.Blocked;
  const { lock, isFree } = useQuoteEditLockState(isWaiting ? quoteId : '');
  const onUnblockedRef = useRef(onUnblocked);
  onUnblockedRef.current = onUnblocked;
  const isFreeRef = useRef(isFree);
  isFreeRef.current = isFree;
  const retakeLockRef = useRef((): void => undefined);

  const { getAccessTokenSilently } = useSlabAuth();
  const acquireLock = useSlabMutation('POST quote edit lock');
  const releaseLock = useSlabMutation('DELETE quote edit lock');

  useEffect(() => {
    if (!enabled || !isValidUUID(quoteId)) return undefined;

    let isActive = true;
    let isBlocked = false;
    let pendingAcquire: Promise<void> = Promise.resolve();
    // pagehide cannot wait for a token, so keep the latest one at hand.
    let accessToken: string | null = null;
    const args = { pathParams: { id: quoteId } };

    const takeLock = (): void => {
      pendingAcquire = acquireLock
        .mutateAsync({ args: { ...args, body: { clientId: pageClientId } } })
        .then(
          (): void => {
            const wasBlocked = isBlocked;
            isBlocked = false;
            if (!isActive) return;

            setStatus(QuoteEditLockStatus.Held);
            if (wasBlocked) onUnblockedRef.current?.();
            getAccessTokenSilently({
              authorizationParams: { audience: SlabConfig.auth0.audience },
            })
              .then((token: string): void => {
                accessToken = token;
              })
              .catch((): void => {});
          },
          (error: unknown): void => {
            if (!isQuoteLockedError(error)) return;

            isBlocked = true;
            if (!isActive) return;

            setBlockedMessage(error.response?.data?.message ?? QUOTE_EDIT_LOCK_FALLBACK_MESSAGE);
            setStatus(QuoteEditLockStatus.Blocked);
          },
        );
    };

    setStatus(QuoteEditLockStatus.Acquiring);
    setBlockedMessage(null);
    takeLock();
    retakeLockRef.current = takeLock;
    // A freed lock is retaken here too, in case the retake on its release failed.
    const heartbeat = window.setInterval(() => {
      if (!isBlocked || isFreeRef.current) takeLock();
    }, QUOTE_EDIT_LOCK_HEARTBEAT_MS);

    // Closing the tab skips the unmount cleanup; keepalive lets the release outlive the page.
    const releaseOnPageHide = (): void => {
      if (isBlocked || !hasValue(accessToken)) return;

      releaseQuoteEditLockOnPageHide(quoteId, accessToken);
    };
    window.addEventListener('pagehide', releaseOnPageHide);

    return (): void => {
      isActive = false;
      retakeLockRef.current = (): void => undefined;
      window.clearInterval(heartbeat);
      window.removeEventListener('pagehide', releaseOnPageHide);
      setStatus(QuoteEditLockStatus.Inactive);
      // A request still in flight can grant the lock after this point, so release only once it settles.
      pendingAcquire.then((): void => {
        if (!isBlocked) releaseLock.mutate({ args: { ...args, body: { clientId: pageClientId } } });
      });
    };
  }, [enabled, quoteId]);

  useEffect(() => {
    if (isWaiting && isFree) retakeLockRef.current();
  }, [isWaiting, isFree]);

  return { status, blockedMessage, editorName: lock?.editorName ?? null };
};
